import { NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { Information } from '@carbon/icons-react';
import { Tooltip } from '@carbon/react';
import { defineMessages, useIntl } from 'react-intl';
import moment from 'moment';

import styles from './EventRecurrence.module.scss';

import { HowLongMode, RecurrenceType } from 'api/dtos/event';
import { useEventContext } from 'Screens/Dashboard/Events/EventHandler/EntityEventContext';
import { getOneYearRecurrenceEndDate, getRecurrenceForType } from 'Screens/Dashboard/Events/EventHandler/inputs/EventRecurrence/recurrenceUtils';
import WeekDayPicker, { SelectedDays } from 'Components/Common/WeekDayPicker/WeekDayPicker';
import { WeekDay } from 'Components/Common/WeekDayPicker/constants';
import EveryDayRecurrenceInput from './EveryDayRecurrence/EveryDayRecurrenceInput.tsx/EveryDayRecurrenceInput';
import WeeklyRecurrenceInput from './WeeklyRecurrence/WeeklyRecurrenceInput/WeeklyRecurrenceInput';
import EveryMonthRecurrenceInput from './MonthlyRecurrence/EveryMonthRecurrenceInput';

const WEEK_DAYS = [WeekDay.Monday, WeekDay.Tuesday, WeekDay.Wednesday, WeekDay.Thursday, WeekDay.Friday, WeekDay.Saturday, WeekDay.Sunday];

const EventRecurrence = () => {
    const intl = useIntl();
    const { values, updateValues } = useEventContext();

    const options = [
        { value: String(RecurrenceType.None), label: intl.formatMessage(messages.none) },
        { value: String(RecurrenceType.Daily), label: intl.formatMessage(messages.daily) },
        { value: String(RecurrenceType.Weekly), label: intl.formatMessage(messages.weekly) },
        { value: String(RecurrenceType.Monthly), label: intl.formatMessage(messages.monthly) },
        { value: String(RecurrenceType.Yearly), label: intl.formatMessage(messages.yearly) },
    ];
    const howLongOptions = [
        { value: 'year', label: intl.formatMessage(messages.oneYear) },
        { value: 'untilDate', label: intl.formatMessage(messages.untilDate) },
        { value: 'times', label: intl.formatMessage(messages.times) },
    ];

    const getOneYearUntilDate = () => getOneYearRecurrenceEndDate();
    const originalRecurrenceEndDate = values.recurrenceEndDate
        ? moment(values.recurrenceEndDate).format('YYYY-MM-DD')
        : undefined;

    const howLongValue = (() => {
        if (values.howLong?.mode === HowLongMode.ForXTimes) {
            return 'times';
        }

        if (values.howLong?.mode === HowLongMode.UntilDate) {
            return 'untilDate';
        }

        return 'year';
    })();

    const onChange = (value: string | null) => {
        const type = Number(value ?? RecurrenceType.None) as RecurrenceType;

        updateValues({
            ...values,
            recurrence: getRecurrenceForType(type, values.dateFrom),
            howLong: type === RecurrenceType.None || type === RecurrenceType.Yearly
                ? undefined
                : { mode: HowLongMode.Always },
        });
    };

    const onHowLongChange = (value: string | null) => {
        if (value === 'times') {
            updateValues({
                ...values,
                howLong: {
                    mode: HowLongMode.ForXTimes,
                    times: values.howLong?.times && values.howLong.times > 0 ? values.howLong.times : 1,
                },
            });
            return;
        }

        if (value === 'untilDate') {
            updateValues({
                ...values,
                howLong: {
                    mode: HowLongMode.UntilDate,
                    untilDate: values.howLong?.untilDate ?? getOneYearUntilDate(),
                },
            });
            return;
        }

        updateValues({
            ...values,
            howLong: { mode: HowLongMode.Always },
        });
    };

    const onUntilDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateValues({
            ...values,
            howLong: {
                mode: HowLongMode.UntilDate,
                untilDate: event.currentTarget.value,
            },
        });
    };

    const onTimesChange = (value: number | string) => {
        updateValues({
            ...values,
            howLong: {
                mode: HowLongMode.ForXTimes,
                times: Number(value) || 0,
            },
        });
    };

    const untilDateError = values.howLong?.mode === HowLongMode.UntilDate
        && howLongValue === 'untilDate'
        && values.howLong.untilDate
        && (
            moment(values.howLong.untilDate).isBefore(moment(values.dateFrom), 'day')
            || moment(values.howLong.untilDate).isAfter(moment(getOneYearUntilDate()), 'day')
        );
    const currentRecurrenceEndDate = values.howLong?.mode === HowLongMode.UntilDate && values.howLong.untilDate
        ? moment(values.howLong.untilDate).format('YYYY-MM-DD')
        : undefined;
    const selectedExcludedDays: SelectedDays = {
        Monday: false,
        Tuesday: false,
        Wednesday: false,
        Thursday: false,
        Friday: false,
        Saturday: false,
        Sunday: false,
    };
    (values.recurrence.daily?.excludedDays ?? []).forEach((day) => {
        if (day >= 0 && day < WEEK_DAYS.length) {
            selectedExcludedDays[WEEK_DAYS[day]] = true;
        }
    });
    const selectedWeeklyDays: SelectedDays = {
        Monday: false,
        Tuesday: false,
        Wednesday: false,
        Thursday: false,
        Friday: false,
        Saturday: false,
        Sunday: false,
    };
    (values.recurrence.weekly?.days ?? []).forEach((day) => {
        if (day >= 0 && day < WEEK_DAYS.length) {
            selectedWeeklyDays[WEEK_DAYS[day]] = true;
        }
    });

    const onExcludedDaysChange = (days: SelectedDays) => {
        const excludedDays = WEEK_DAYS.flatMap((day, index) => (days[day] ? [index] : []));

        if (excludedDays.length === WEEK_DAYS.length) {
            return;
        }

        updateValues({
            recurrence: {
                ...values.recurrence,
                daily: {
                    ...values.recurrence.daily,
                    excludedDays,
                },
            },
        });
    };
    const onWeeklyDaysChange = (days: SelectedDays) => {
        const selectedDays = WEEK_DAYS.flatMap((day, index) => (days[day] ? [index] : []));

        updateValues({
            recurrence: {
                ...values.recurrence,
                weekly: {
                    interval: values.recurrence.weekly?.interval ?? 1,
                    days: selectedDays,
                },
            },
        });
    };
    const showRecurrenceEndDateWarning = values.isEdit
        && values.recurrence.type !== RecurrenceType.None
        && Boolean(originalRecurrenceEndDate)
        && (
            values.howLong?.mode !== HowLongMode.UntilDate ||
            currentRecurrenceEndDate !== originalRecurrenceEndDate
        );
    const showHowLongSettings = values.recurrence.type !== RecurrenceType.None
        && values.recurrence.type !== RecurrenceType.Yearly;

    return (
        <div className={styles.container}>
            <Stack gap="sm">
                <Select
                    id="event-recurrence-select"
                    label={(
                        <span className={styles.labelWithTooltip}>
                            {intl.formatMessage(messages.label)}
                            <Tooltip
                                label={intl.formatMessage(messages.recurrenceScopeTooltip)}
                                align="right"
                            >
                                <Information size={16} />
                            </Tooltip>
                        </span>
                    )}
                    data={options}
                    value={String(values.recurrence.type)}
                    onChange={onChange}
                    radius="md"
                    size="md"
                    allowDeselect={false}
                    comboboxProps={{ withinPortal: false }}
                />
                {values.recurrence.type === RecurrenceType.Daily && (
                    <div className={styles.dailyExcept}>
                        <EveryDayRecurrenceInput />
                        <Text size="sm" fw={500}>{intl.formatMessage(messages.dailyExcept)}</Text>
                        <Text size="xs">{intl.formatMessage(messages.dailyExceptHint)}</Text>
                        <div className={styles.dailyExceptDays}>
                            <WeekDayPicker
                                selectedDays={selectedExcludedDays}
                                onValueChange={onExcludedDaysChange}
                                allowEmpty
                                allowAll={false}
                                excludedDaysMode
                            />
                        </div>
                    </div>
                )}
                {values.recurrence.type === RecurrenceType.Weekly && (
                    <div className={styles.dailyExcept}>
                        <WeeklyRecurrenceInput />
                        <WeekDayPicker
                            selectedDays={selectedWeeklyDays}
                            onValueChange={onWeeklyDaysChange}
                        />
                    </div>
                )}
                {values.recurrence.type === RecurrenceType.Monthly && (
                    <EveryMonthRecurrenceInput />
                )}
                {showHowLongSettings && (
                    <div className={styles.howLongSettings}>
                        <Select
                            id="event-recurrence-how-long-select"
                            label={intl.formatMessage(messages.howLong)}
                            data={howLongOptions}
                            value={howLongValue}
                            onChange={onHowLongChange}
                            radius="md"
                            size="md"
                            allowDeselect={false}
                            comboboxProps={{ withinPortal: false }}
                        />
                        {howLongValue === 'untilDate' && (
                            <TextInput
                                id="event-recurrence-until-date"
                                type="date"
                                label={intl.formatMessage(messages.untilDate)}
                                value={values.howLong?.untilDate ?? ''}
                                onChange={onUntilDateChange}
                                min={moment(values.dateFrom).format('YYYY-MM-DD')}
                                max={getOneYearUntilDate()}
                                error={untilDateError ? intl.formatMessage(messages.untilDateError) : undefined}
                                radius="md"
                                size="md"
                            />
                        )}
                        {howLongValue === 'times' && (
                            <NumberInput
                                id="event-recurrence-times"
                                label={intl.formatMessage(messages.timesCount)}
                                min={1}
                                value={values.howLong?.times ?? 1}
                                onChange={onTimesChange}
                                radius="md"
                                size="md"
                            />
                        )}
                        {showRecurrenceEndDateWarning && (
                            <div className={styles.warningText}>
                                {intl.formatMessage(messages.recurrenceEndDateWarning)}
                            </div>
                        )}
                    </div>
                )}
            </Stack>
        </div>
    );
};

const messages = defineMessages({
    label: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventRecurrence.EventRecurrence.label',
        defaultMessage: 'Powtarzanie',
    },
    none: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventRecurrence.EventRecurrence.none',
        defaultMessage: 'Nie powtarzaj',
    },
    daily: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventRecurrence.EventRecurrence.daily',
        defaultMessage: 'Codziennie',
    },
    dailyExcept: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventRecurrence.EventRecurrence.dailyExcept',
        defaultMessage: 'Codziennie oprócz',
    },
    dailyExceptHint: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventRecurrence.EventRecurrence.dailyExceptHint',
        defaultMessage: 'Niebieskie dni są aktywne. Dni oznaczone × są wykluczone. Pozostaw co najmniej jeden aktywny dzień.',
    },
    weekly: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventRecurrence.EventRecurrence.weekly',
        defaultMessage: 'Tygodniowo',
    },
    monthly: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventRecurrence.EventRecurrence.monthly',
        defaultMessage: 'Miesięcznie',
    },
    yearly: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventRecurrence.EventRecurrence.yearly',
        defaultMessage: 'Co rok',
    },
    howLong: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventRecurrence.EventRecurrence.howLong',
        defaultMessage: 'Koniec powtarzania',
    },
    oneYear: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventRecurrence.EventRecurrence.oneYear',
        defaultMessage: 'Za rok',
    },
    untilDate: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventRecurrence.EventRecurrence.untilDate',
        defaultMessage: 'Do daty',
    },
    times: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventRecurrence.EventRecurrence.times',
        defaultMessage: 'X razy',
    },
    timesCount: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventRecurrence.EventRecurrence.timesCount',
        defaultMessage: 'Liczba powtórzeń',
    },
    untilDateError: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventRecurrence.EventRecurrence.untilDateError',
        defaultMessage: 'Data musi być pomiędzy początkiem wydarzenia a datą przypadającą rok od dzisiaj.',
    },
    recurrenceEndDateWarning: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventRecurrence.EventRecurrence.recurrenceEndDateWarning',
        defaultMessage: 'Zmiana końca powtarzania zostanie zastosowana do całej serii.',
    },
    recurrenceScopeTooltip: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventRecurrence.EventRecurrence.recurrenceScopeTooltip',
        defaultMessage: 'Zmiana powtarzania lub jego ustawień w istniejącej serii zawsze dotyczy całej serii.',
    },
});

export default EventRecurrence;
